import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

interface PermissionsErrorProps {
  type: "feature" | "permission";
  feature: PartnerFeatureEnum | null;
}

export const PermissionsError = ({ type, feature }: PermissionsErrorProps) => {
  const { t } = useTranslation();
  const { hasOrgFeature } = useOrgPermissions();

  const getFeatureLabel = (featureEnum: PartnerFeatureEnum | null): string => {
    if (!featureEnum) return "";

    switch (featureEnum) {
      case PartnerFeatureEnum.BulkMessaging:
        return t("global.features.bulkMessaging");
      case PartnerFeatureEnum.Games:
        return t("global.features.automatedGames");
      case PartnerFeatureEnum.Rise:
        return t("global.features.RISE");
      case PartnerFeatureEnum.RisePredictiveModels:
        return t("global.features.RisePredictiveModels");
      case PartnerFeatureEnum.RiseStrategyBoard:
        return t("global.features.RiseStrategyBoard");
      default:
        return String(featureEnum).toLowerCase();
    }
  };

  const hasRISE = hasOrgFeature(PartnerFeatureEnum.Rise);
  const hasRISEModels = hasOrgFeature(PartnerFeatureEnum.RisePredictiveModels);
  const waitingOnModels =
    (feature === PartnerFeatureEnum.RisePredictiveModels ||
      feature === PartnerFeatureEnum.Rise) &&
    hasRISE &&
    !hasRISEModels;

  const featureLabel = getFeatureLabel(feature);

  const getTitle = (): string => {
    if (waitingOnModels) return t("upgrade.features.modelsTitle");
    return type === "feature"
      ? t("upgrade.features.title", {
          feature: featureLabel,
        })
      : t("upgrade.permission.title");
  };

  const getMessage = (): React.ReactNode => {
    if (waitingOnModels) return t("upgrade.features.modelsMessage");
    return type === "feature" ? (
      <Trans
        i18nKey="upgrade.features.message"
        values={{ feature: featureLabel }}
        components={{
          supportLink: (
            <a
              href="mailto:support@zeemee.com"
              className="text-blue-primary hover:underline"
            />
          ),
        }}
      />
    ) : (
      <Trans
        i18nKey="upgrade.permission.message"
        components={{
          supportLink: (
            <a
              href="mailto:support@zeemee.com"
              className="text-blue-primary hover:underline"
            />
          ),
        }}
      />
    );
  };

  return (
    <div className="flex h-full items-center justify-center py-12">
      <div className="text-center">
        <h2 className="mb-3 text-2xl font-medium text-foreground">
          {getTitle()}
        </h2>
        <div className="mb-8 text-sm text-muted-foreground">{getMessage()}</div>
      </div>
    </div>
  );
};
